import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { MuiThemeProvider } from './context/MuiThemeProvider';
import { ImageToolsOverlayProvider } from './context/ImageToolsOverlayContext';
import { QueryBuilderOverlayProvider } from './context/QueryBuilderOverlayContext';
import { ConeSearchOverlayProvider } from './context/ConeSearchOverlayContext';
import { QueryHistoryOverlayProvider } from './context/QueryHistoryOverlayContext';

// Common components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import QueryBuilder from './pages/QueryBuilder';
import QueryHistory from './pages/QueryHistory';
import QueryView from './pages/QueryView';
import SchemaExplorer from './pages/SchemaExplorer';
import Dashboard from './pages/Dashboard';
import UserProfile from './pages/UserProfile';
import ErrorPage from './pages/Error';
import ConeSearch from './pages/ConeSearch';
import ObjectDetail from './pages/ObjectDetail';
import ImageBrowser from './components/ImageBrowser';
import ImageTools from './pages/ImageTools';
import News from './pages/News';
import NewsPostDetail from './pages/NewsPostDetail';
import Tools from './pages/Tools';
import Documentation from './pages/Documentation';
import FAQ from './pages/FAQ';
import FAQDetail from './pages/FAQDetail';
import SharedNotes from './pages/SharedNotes';
import SharedNoteDetail from './pages/SharedNoteDetail';
import InteractiveViewer from './pages/InteractiveViewer';
import CreatePrompt from './pages/CreatePrompt';

// Admin pages
import AdminDashboard from './pages/Admin/Dashboard';
import UserManagement from './pages/Admin/UserManagement';
import RoleManagement from './pages/Admin/RoleManagement';
import PermissionManagement from './pages/Admin/PermissionManagement';
import AstroTablesManagement from './pages/Admin/AstroTablesManagement';
import ImageCollections from './components/ImageCollections';
import NewsManagement from './pages/Admin/NewsManagement';
import DocumentationManagement from './components/documentation/DocumentationManagement';
import DocumentationDetail from './components/documentation/DocumentationDetail';

import BackgroundBlobs from './components/common/Background';
import ImageToolsOverlay from './components/ImageToolsOverlay';
import QueryBuilderOverlay from './components/QueryBuilderOverlay';
import ConeSearchOverlay from './components/ConeSearchOverlay';
import QueryHistoryOverlay from './components/QueryHistoryOverlay';

const ProtectedRoute = ({ element, requiredAuth = true, requiredAdmin = false }) => {
  const { isAuthenticated, isStaff, isSuperuser, loading } = useAuth();

  // Enquanto o AuthProvider ainda está carregando o usuário/token,
  // não redireciona ninguém
  if (loading) {
    return <div>Loading...</div>;
    // ou um spinner/skeleton
  }

  // Primeiro verifica se precisa estar autenticado
  if (requiredAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Depois verifica admin
  if (requiredAdmin && !(isStaff() || isSuperuser())) {
    return <Navigate to="/dashboard" replace />;
  }

  // Se a rota é pública, mas o usuário já está logado
  if (!requiredAuth && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return element;
};

const App = () => {
  return (
    <ThemeProvider>
      <MuiThemeProvider>
        <AuthProvider>
          <ImageToolsOverlayProvider>
            <QueryBuilderOverlayProvider>
              <ConeSearchOverlayProvider>
                <QueryHistoryOverlayProvider>
              <Router>
              <div className="relative isolate flex flex-col min-h-screen bg-ui-light-background dark:bg-ui-dark-background text-neutral-900 dark:text-white transition-colors">
              <BackgroundBlobs count={7} />
              <Navbar />
              <ImageToolsOverlay />
              <QueryBuilderOverlay />
              <ConeSearchOverlay />
              <QueryHistoryOverlay />
          
          <main className="pt-28 flex-grow px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<ProtectedRoute element={<Login />} requiredAuth={false} />} />
              <Route path="/register" element={<ProtectedRoute element={<Register />} requiredAuth={false} />} />
              <Route path="/reset-password" element={<ProtectedRoute element={<ResetPassword />} requiredAuth={false} />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/query" element={<QueryBuilder />} />
              <Route path="/database" element={<SchemaExplorer />} />
              <Route path="/cone-search" element={<ConeSearch />} />
              <Route path="/images" element={<ImageBrowser />} />
              <Route path="/image-tools" element={<ImageTools />} />
              <Route path="/interactive-viewer" element={<InteractiveViewer />} />
              <Route path="/create-prompt" element={<CreatePrompt />} />
              <Route path="/object/:schemaTable/:idColumn/:idValue" element={<ObjectDetail />} />
              <Route path="/object-coords/:schemaTable/:ra/:dec" element={<ObjectDetail />} />

              {/* Protected routes (require authentication) */}
              <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
              <Route path="/queries/history" element={<ProtectedRoute element={<QueryHistory />} />} />
              <Route path="/queries/:queryId" element={<ProtectedRoute element={<QueryView />} />} />
              <Route path="/profile" element={<ProtectedRoute element={<UserProfile />} />} />
              <Route path="/news" element={<ProtectedRoute element={<News />} />} />
              <Route path="/news/:postId" element={<ProtectedRoute element={<NewsPostDetail />} />} />
              <Route path="/faq/:questionId" element={<FAQDetail />} />
              <Route path="/shared-notes" element={<ProtectedRoute element={<SharedNotes />} />} />
              <Route path="/shared-notes/:noteId" element={<ProtectedRoute element={<SharedNoteDetail />} />} />
              
              {/* Admin routes */}
              <Route path="/admin" element={<ProtectedRoute element={<AdminDashboard />} requiredAdmin={true} />} />
              <Route path="/admin/users" element={<ProtectedRoute element={<UserManagement />} requiredAdmin={true} />} />
              <Route path="/admin/users/:userId" element={<ProtectedRoute element={<UserManagement />} requiredAdmin={true} />} />
              <Route path="/admin/roles" element={<ProtectedRoute element={<RoleManagement />} requiredAdmin={true} />} />
              <Route path="/admin/permissions/:roleId" element={<ProtectedRoute element={<PermissionManagement />} requiredAdmin={true} />} />
              <Route path="/admin/astro-tables" element={<ProtectedRoute element={<AstroTablesManagement />} requiredAdmin={true} />} />
              <Route path="/admin/images" element={<ProtectedRoute element={<ImageCollections />} requiredAdmin={true} />} />
              <Route path="/admin/news" element={<ProtectedRoute element={<NewsManagement />} requiredAdmin={true} />} />
              <Route path="/admin/documentation" element={<ProtectedRoute element={<DocumentationManagement />} requiredAdmin={true} />} />
              <Route path="/documentation/route/:postId" element={<DocumentationDetail />} />
              <Route path="/documentation/:routeName" element={<DocumentationDetail />} />
              
              {/* Error handling */}
              <Route path="/error" element={<ErrorPage />} />
              <Route path="*" element={<Navigate to="/error" replace />} />
            </Routes>
          </main>
          
            <Footer />
          </div>
              </Router>
                </QueryHistoryOverlayProvider>
              </ConeSearchOverlayProvider>
            </QueryBuilderOverlayProvider>
          </ImageToolsOverlayProvider>
        </AuthProvider>
      </MuiThemeProvider>
    </ThemeProvider>
  );
};

export default App;
